/**
 * water_lighting — ONE lighting rule for every water body (2026-09-08).
 *
 * The sea, the lakes and the rivers all wear the engine's realistic water;
 * they must also agree on how that water follows the sun. Illuminance scales
 * every authored radiance term with the sun's elevation (a moonlit floor);
 * the sky-side tint is what the flat reflection fallback shows — warm around
 * dawn and dusk, deep navy at night. Pure; the modules copy the results into
 * their material uniforms each frame.
 */
import * as THREE from 'three/webgpu';
import { EXPOSURE_REF, lum709, type Rgb } from './photometry.js';

export const WATER_DAY_TINT = 0x87a8c3;
export const WATER_DUSK_TINT = 0xd9975e;
export const WATER_NIGHT_TINT = 0x0c1626;
const dayTint = new THREE.Color(WATER_DAY_TINT);
const duskTint = new THREE.Color(WATER_DUSK_TINT);

/** Authored water radiance 0..1 from the sun's Y (floor 0.04 = a moonlit sea). */
export function waterIlluminance(sunY: number): number {
  return Math.max(0.04, Math.min(1, (sunY + 0.05) / 0.25));
}

/** Sky-side fallback tint for the hour (written into `out`, returned). */
export function waterSkyTint(sunY: number, out: THREE.Color): THREE.Color {
  const dayness = Math.max(0, Math.min(1, (sunY - 0.02) / 0.3));
  const duskness = Math.max(0, 1 - Math.abs(sunY - 0.04) / 0.14); // peaks near the horizon
  return out.setHex(WATER_NIGHT_TINT).lerp(dayTint, dayness).lerp(duskTint, duskness * 0.65);
}

// ── THE PHYSICAL WATER LIGHT (2026-09-26, water session 3 — the lighting agent's recipe, rule 3) ──────────────
//
// The rule above lit the water by the sun's ELEVATION on a fixed 0..1 scale while the ground beside it moved to the
// photometric curve (LightingModule: every light in scene units = lux × luxToScene): the water sat on its own curve —
// at a low sun its authored terms kept 55 % of noon where the ground kept ~45 %, and at night 2× the ground's floor.
// Now the water reads the SAME numbers the ground is lit by (`lightFrame.keyScene` / `skyScene`, written by
// LightingModule at RenderSub/4, before the water at 36):
//   illum     = the horizontal GLOBAL illuminance (key·sin el + sky) in scene units ÷ WATER_REF_SCENE — the diffuse-lit
//               terms (the body's colour, foam, bubbles, the flat sky fallback, the bed's ambient, caustics). Exactly
//               the ground's Lambert factor, so the water and the ground beside it share one curve by construction.
//   sun (RGB) = the key's NORMAL illuminance (its colour × intensity) ÷ WATER_REF_SCENE — the sun-driven terms (the
//               glint, the crest/body scattering, the bed's direct light; the engine's `physicalSun` + `uSunLight`).
// WATER_REF_SCENE = π / EXPOSURE_REF is the curve's clear-noon horizontal global in scene units: a scene of evScene
// shows (π / EXPOSURE_REF)·2^(−drop) (lux × luxToScene with ev100 = evScene + drop), so illum = 2^(−drop) × the
// tier's lighting boost — 1 at the noon the look was tuned under, 2^(−5.5) at night.
// Before LightingModule's first frame (or with no light model) the old elevation rule stands in.

/** The clear-noon horizontal global illuminance in scene units — the reference the water's authored terms were tuned under. */
export const WATER_REF_SCENE = Math.PI / EXPOSURE_REF;
/** The authored sun colour the fallback lights with (the engine material's default `sunColor`). */
export const WATER_SUN_FALLBACK = 0xfff4e0;
const sunFallback = new THREE.Color(WATER_SUN_FALLBACK);

export interface WaterLight {
  /** The diffuse-lit terms' factor (the material's `uIlluminance`). */
  illum: number;
  /** The key light for the sun-driven terms (the material's `uSunLight`), RGB. */
  sun: { r: number; g: number; b: number };
  /** true = read off the photometric light frame; false = the elevation fallback. */
  physical: boolean;
}

export interface WaterLightFrame { valid: boolean; sinEl: number; keyScene: Rgb; skyScene: Rgb }

const finite = (v: number): boolean => Number.isFinite(v) && v >= 0;

/** The water's light this frame from the photometric light frame (writes `out`). `sunY` feeds only the fallback. */
export function waterLight(frame: WaterLightFrame, sunY: number, out: WaterLight): WaterLight {
  const k = frame.keyScene, s = frame.skyScene;
  const keyLum = lum709(k.x, k.y, k.z), skyLum = lum709(s.x, s.y, s.z);
  const sinEl = Math.max(0, frame.sinEl);
  if (frame.valid && finite(keyLum) && finite(skyLum) && Number.isFinite(frame.sinEl) && keyLum + skyLum > 0) {
    out.illum = (keyLum * sinEl + skyLum) / WATER_REF_SCENE;
    out.sun.r = k.x / WATER_REF_SCENE; out.sun.g = k.y / WATER_REF_SCENE; out.sun.b = k.z / WATER_REF_SCENE;
    out.physical = true;
    return out;
  }
  const illum = waterIlluminance(sunY);
  out.illum = illum;
  out.sun.r = sunFallback.r * illum; out.sun.g = sunFallback.g * illum; out.sun.b = sunFallback.b * illum;
  out.physical = false;
  return out;
}

/** A fresh `WaterLight` (one per owner; `waterLight` fills it every frame). */
export function makeWaterLight(): WaterLight { return { illum: 1, sun: { r: 1, g: 1, b: 1 }, physical: false }; }
